"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import "./AddNewConsultation.css";

import TreatmentPlan from "../TreatmentPlan/TreatmentPlan";
import DocumentPreview from "../DocumentPreview/DocumentPreview";

import { usePrescription } from "../../../../prescriptions/hooks/usePrescription";
import { usePrescriptionLayout } from "../../../../prescriptions/hooks/usePrescriptionLayout";
import { updateConsultation } from "../../../../consultation/redux/thunks/consultationThunks";
import { fetchDoctorInfoByDoctorId } from "../../../../ProfileDoctor/redux/thunks/doctorProfileThunks";
import { fetchMedicalInfoByUserId } from "../../../../Profile/redux/thunks/medicalThunks";

/* =========================================================
   HELPERS
========================================================= */

const normalizeId = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === "object") {
    const id = value.id || value.consultationId || value._id || null;
    return id ? String(id).trim() : null;
  }

  const result = String(value).trim();
  return result || null;
};

const EMPTY_DOCUMENT_TEXTS = {
  prescription: "",
  bilan: "",
  radio: "",
  lettre: "",
};

const DOCUMENT_KEYS = [
  "prescription",
  "bilan",
  "radio",
  "lettre",
];

const normalizeDocumentTexts = (value) => ({
  prescription: String(value?.prescription || "").trim(),
  bilan: String(value?.bilan || "").trim(),
  radio: String(value?.radio || "").trim(),
  lettre: String(value?.lettre || "").trim(),
});

const sortGeneratedDocuments = (documents) =>
  [...documents].sort(
    (first, second) =>
      DOCUMENT_KEYS.indexOf(first.type) -
      DOCUMENT_KEYS.indexOf(second.type)
  );

/* Coefficient KC d'un acte : nombre > 0, sinon 1 */
const getActKey = (act) => {
  const value = Number(String(act?.key ?? "").replace(",", "."));

  return Number.isFinite(value) && value > 0 ? value : 1;
};

const AddNewConsultation = ({
  isOpen,
  onClose,
  onPrintRecord,
  onSaved,
  patientData,
  insuranceData,
  emergencyData,
  doctorId: doctorIdProp,
  patientId: patientIdProp,
  consultationId: consultationIdProp,
  frontOnly = false,
  initialDiagnosticText = "",
  initialDocumentTexts = null,
  initialDocumentKey = null,
  initialSavedDocuments = null,
}) => {
  const currentUser = useSelector(
    (state) => state.doctor?.user || null
  );

  const doctorInfo = useSelector(
    (state) =>
      state.doctorProfile?.doctorInfo?.data ||
      state.doctorProfile?.byId?.data ||
      state.profileDoctor?.profile ||
      null
  );

  const doctorId = normalizeId(
    doctorIdProp ||
      currentUser?.pgId ||
      currentUser?.doctorId ||
      currentUser?.id ||
      currentUser?._id
  );

  const dispatch = useDispatch();

  /* =======================================================
     PRESCRIPTION API
  ======================================================= */

  const {
    createPrescriptionForPatient,
    updatePrescriptionForPatient,
  } = usePrescription();

  /* =======================================================
     LAYOUT
  ======================================================= */

  const {
    layout:
      prescriptionLayout,
  } = usePrescriptionLayout();

  /* =======================================================
     STATES
  ======================================================= */

  const [
    prescriptionContent,
    setPrescriptionContent,
  ] = useState("");

  const [
    selectedDocKey,
    setSelectedDocKey,
  ] = useState(
    "prescription"
  );

  const [
    documentDraftTexts,
    setDocumentDraftTexts,
  ] = useState({
    ...EMPTY_DOCUMENT_TEXTS,
  });

  const [
    documentSavedState,
    setDocumentSavedState,
  ] = useState({});

  const [
    generatedDocuments,
    setGeneratedDocuments,
  ] = useState([]);

  const [medicalActs, setMedicalActs] = useState([]);
  const [consultationPrice, setConsultationPrice] = useState(0);

  const [
    activeGeneratedDocumentId,
    setActiveGeneratedDocumentId,
  ] = useState(null);

  const [
    isSavingConsultation,
    setIsSavingConsultation,
  ] = useState(false);

  const [
    saveConsultationError,
    setSaveConsultationError,
  ] = useState("");

  const [loadedPatientMedical, setLoadedPatientMedical] = useState(null);
  const [loadedDoctorInfo, setLoadedDoctorInfo] = useState(null);

  /* =======================================================
     PARAMS
  ======================================================= */

  const params =
    useParams();

  const { t } = useTranslation();

  const patientIdFromParams =
    normalizeId(
      params?.patientId
    ) ||
    normalizeId(
      params?.userId
    ) ||
    normalizeId(
      params?.id
    ) ||
    null;

  /* =======================================================
     PATIENT ID
  ======================================================= */

  const resolvedPatientId =
    useMemo(
      () =>
        normalizeId(
          patientIdProp
        ) ||
        normalizeId(
          patientData?.id
        ) ||
        normalizeId(
          patientData?.userId
        ) ||
        normalizeId(
          patientData?._id
        ) ||
        normalizeId(
          patientData?.pgId
        ) ||
        patientIdFromParams ||
        null,
      [
        patientIdProp,
        patientData,
        patientIdFromParams,
      ]
    );

  const patientMedical = useSelector((state) => {
    const medicalState = state.medical || {};
    const id = resolvedPatientId ? String(resolvedPatientId) : "";

    return (
      medicalState.byUserId?.[id] ||
      medicalState.byUserId?.[id.toLowerCase()] ||
      medicalState.data ||
      null
    );
  });

  const consultationInsuranceData = useMemo(
    () => ({
      ...insuranceData,
      insurance:
        insuranceData?.insurance ||
        loadedPatientMedical?.insurance ||
        patientMedical?.insurance ||
        [],
      provider:
        insuranceData?.provider ||
        loadedPatientMedical?.insurance?.join?.(", ") ||
        patientMedical?.insurance?.join?.(", ") ||
        null,
    }),
    [insuranceData, loadedPatientMedical, patientMedical]
  );

  /* =======================================================
     CONSULTATION ID
  ======================================================= */

  const resolvedConsultationId =
    useMemo(
      () =>
        normalizeId(
          consultationIdProp
        ),
      [
        consultationIdProp,
      ]
    );

  useEffect(() => {
    if (isOpen && doctorId) {
      dispatch(fetchDoctorInfoByDoctorId(doctorId))
        .unwrap()
        .then((record) => setLoadedDoctorInfo(record || null))
        .catch(() => setLoadedDoctorInfo(null));
    }
  }, [dispatch, doctorId, isOpen]);

  useEffect(() => {
    if (!isOpen || !resolvedPatientId) return;

    let active = true;
    dispatch(fetchMedicalInfoByUserId(resolvedPatientId))
      .unwrap()
      .then((record) => {
        if (active) setLoadedPatientMedical(record || null);
      })
      .catch(() => {
        if (active) setLoadedPatientMedical(null);
      });

    return () => {
      active = false;
    };
  }, [dispatch, isOpen, resolvedPatientId]);

  /* =======================================================
     BODY / RESET
  ======================================================= */

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow =
        "hidden";

      return;
    }

    document.body.style.overflow =
      "";

    setPrescriptionContent("");

    setSelectedDocKey(
      "prescription"
    );

    setDocumentDraftTexts({
      ...EMPTY_DOCUMENT_TEXTS,
    });

    setDocumentSavedState(
      {}
    );

    setGeneratedDocuments(
      []
    );

    setActiveGeneratedDocumentId(
      null
    );

    setIsSavingConsultation(
      false
    );

    setSaveConsultationError(
      ""
    );
  }, [
    isOpen,
  ]);

  useEffect(() => {
    return () => {
      document.body.style.overflow =
        "";
    };
  }, []);

  /* =======================================================
     INITIAL VALUES
  ======================================================= */

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextTexts =
      normalizeDocumentTexts(
        initialDocumentTexts
      );

    setDocumentDraftTexts(
      nextTexts
    );

    setDocumentSavedState(
      initialSavedDocuments ||
      {}
    );

    setSelectedDocKey(
      "prescription"
    );

    setPrescriptionContent(
      nextTexts.prescription ||
      ""
    );

    setGeneratedDocuments(
      []
    );

    setActiveGeneratedDocumentId(
      null
    );

    setSaveConsultationError(
      ""
    );
  }, [
    isOpen,
    initialDiagnosticText,
    initialDocumentTexts,
    initialSavedDocuments,
  ]);

  /* =======================================================
     PREVIEW
  ======================================================= */

  const handlePreviewChange =
    useCallback(
      (content) => {
        const next =
          String(
            content || ""
          );

        setPrescriptionContent(
          next
        );

        setDocumentDraftTexts(
          (previous) => ({
            ...previous,

            [selectedDocKey]:
              next,
          })
        );

        setActiveGeneratedDocumentId(
          null
        );
      },
      [
        selectedDocKey,
      ]
    );

  const handleDocumentTextsChange =
    useCallback(
      (texts) => {
        const normalizedTexts = normalizeDocumentTexts(texts);

        setDocumentDraftTexts((previous) => {
          const isEqual = DOCUMENT_KEYS.every(
            (key) => previous?.[key] === normalizedTexts[key]
          );

          return isEqual ? previous : normalizedTexts;
        });

        setPrescriptionContent(
          normalizedTexts[selectedDocKey] || ""
        );
      },
      [selectedDocKey]
    );

  /* =======================================================
     CHANGE TYPE
  ======================================================= */

  const handleDocKeyChange =
    useCallback(
      (key) => {
        if (
          !DOCUMENT_KEYS.includes(
            key
          )
        ) {
          return;
        }

        setSelectedDocKey(
          key
        );

        setActiveGeneratedDocumentId(
          null
        );

        const generated =
          generatedDocuments.find(
            (document) =>
              document.type ===
              key
          );

        if (generated) {
          setActiveGeneratedDocumentId(
            generated.id
          );

          setPrescriptionContent(
            generated.content ||
            ""
          );

          return;
        }

        setActiveGeneratedDocumentId(
          null
        );

        setPrescriptionContent(
          documentDraftTexts?.[
            key
          ] ||
          ""
        );
      },
      [
        generatedDocuments,
        documentDraftTexts,
      ]
    );

  /* =======================================================
     GENERATE
  ======================================================= */

  const handleGenerateDocument =
    useCallback(
      (documentData) => {
        const type =
          String(
            documentData?.type ||
            ""
          ).trim();

        if (
          !DOCUMENT_KEYS.includes(
            type
          )
        ) {
          return;
        }

        const content =
          String(
            documentData
              ?.content ||
            ""
          ).trim();

        if (!content) {
          return;
        }

        const id =
          `generated-${type}`;

        const now =
          new Date()
            .toISOString();

        setGeneratedDocuments(
          (previous) => {
            const existing =
              previous.find(
                (document) =>
                  document.type ===
                  type
              );

            const nextDocument = {
              ...existing,
              ...documentData,

              id,
              type,
              content,

              createdAt:
                existing
                  ?.createdAt ||
                now,

              updatedAt:
                now,
            };

            const next =
              existing
                ? previous.map(
                    (document) =>
                      document.type ===
                      type
                        ? nextDocument
                        : document
                  )
                : [
                    ...previous,
                    nextDocument,
                  ];

            return sortGeneratedDocuments(
              next
            );
          }
        );

        setDocumentDraftTexts(
          (previous) => ({
            ...previous,

            [type]:
              content,
          })
        );

        setSelectedDocKey(
          type
        );

        setPrescriptionContent(
          content
        );

        setActiveGeneratedDocumentId(
          id
        );
      },
      []
    );

  const handleMedicalActsChange = useCallback(
    ({ medicalActs: nextMedicalActs = [] } = {}) => {
      setMedicalActs(nextMedicalActs);
    },
    []
  );

  const handleConsultationPriceChange = useCallback((nextPrice) => {
    setConsultationPrice(Number(nextPrice) || 0);
  }, []);

  /* =======================================================
     CLEAR
  ======================================================= */

  const handleClearGeneratedDocument =
    useCallback(
      (documentType) => {
        const type =
          String(
            documentType ||
            ""
          ).trim();

        if (
          !DOCUMENT_KEYS.includes(
            type
          )
        ) {
          return;
        }

        const generatedId =
          `generated-${type}`;

        setGeneratedDocuments(
          (previous) =>
            previous.filter(
              (document) =>
                document.type !==
                type
            )
        );

        setActiveGeneratedDocumentId(
          (previous) =>
            previous ===
            generatedId
              ? null
              : previous
        );

        setDocumentDraftTexts(
          (previous) => ({
            ...previous,

            [type]:
              "",
          })
        );

        if (
          selectedDocKey ===
          type
        ) {
          setPrescriptionContent(
            ""
          );
        }
      },
      [
        selectedDocKey,
      ]
    );

  /* =======================================================
     PRINT
  ======================================================= */

  const handlePrintRecordClick =
    useCallback(
      (document) => {
        if (!document) {
          return;
        }

        onPrintRecord?.(
          document
        );
      },
      [
        onPrintRecord,
      ]
    );

  /* =======================================================
     SAVE DOCUMENTS
  ======================================================= */

  const handleSavePrescriptionToBackend =
    useCallback(
      async ({
        documentTexts,
      } = {}) => {
        const normalizedTexts =
          normalizeDocumentTexts(
            documentTexts
          );

        /* ===============================================
           FRONT ONLY
        =============================================== */

        if (frontOnly) {
          const saved =
            {};

          DOCUMENT_KEYS.forEach(
            (key) => {
              saved[key] = {
                status:
                  "draft",

                id:
                  null,

                text:
                  normalizedTexts[
                    key
                  ],
              };
            }
          );

          setDocumentDraftTexts(
            normalizedTexts
          );

          setDocumentSavedState(
            saved
          );

          return {
            success:
              true,

            frontOnly:
              true,

            saved,

            warnings:
              [],
          };
        }

        /* ===============================================
           VALIDATE IDS
        =============================================== */

        if (
          !resolvedPatientId
        ) {
          throw new Error(
            "Patient ID is missing."
          );
        }

        if (
          !resolvedConsultationId
        ) {
          throw new Error(
            "Consultation ID is missing."
          );
        }

        const saved =
          {};

        const warnings =
          [];

        let success =
          true;

        for (
          const key of
          DOCUMENT_KEYS
        ) {
          const value =
            normalizedTexts[
              key
            ];

          const existingId =
            normalizeId(
              documentSavedState?.[
                key
              ]?.id
            );

          if (
            !existingId &&
            !value
          ) {
            saved[key] =
              documentSavedState?.[
                key
              ] ||
              null;

            continue;
          }

          try {
            let response;

            if (
              existingId
            ) {
              response =
                await updatePrescriptionForPatient(
                  {
                    prescriptionId:
                      existingId,

                    data: {
                      description:
                        value,

                      documentType:
                        key,
                    },
                  }
                );
            } else {
              response =
                await createPrescriptionForPatient(
                  {
                    patientId:
                      resolvedPatientId,

                    consultationId:
                      resolvedConsultationId,

                    description:
                      value,

                    documentType:
                      key,
                  }
                );
            }

            saved[key] = {
              status:
                "saved",

              id:
                response?.id ||
                response
                  ?.data?.id ||
                existingId ||
                null,

              text:
                value,
            };
          } catch (
            error
          ) {
            console.error(
              `❌ SAVE ${key}:`,
              error
            );

            success =
              false;

            const message =
              error?.response
                ?.data
                ?.message ||
              error?.message ||
              "Save unavailable";

            saved[key] = {
              status:
                "failed",

              id:
                existingId ||
                null,

              text:
                value,

              message,
            };

            warnings.push(
              `${key}: ${message}`
            );
          }
        }

        setDocumentDraftTexts(
          normalizedTexts
        );

        setDocumentSavedState(
          saved
        );

        return {
          success,
          saved,
          warnings,
        };
      },
      [
        frontOnly,
        resolvedPatientId,
        resolvedConsultationId,
        documentSavedState,
        createPrescriptionForPatient,
        updatePrescriptionForPatient,
      ]
    );

  /* =======================================================
     FINAL SAVE

     IMPORTANT :
     la consultation existe déjà.
     Aucun nouveau POST /consultations.
  ======================================================= */

  const handleSaveWholeConsultation =
    useCallback(
      async () => {
        if (
          isSavingConsultation
        ) {
          return;
        }

        setIsSavingConsultation(
          true
        );

        setSaveConsultationError(
          ""
        );

        try {
          if (
            !frontOnly &&
            !resolvedConsultationId
          ) {
            throw new Error(
              "Consultation ID is missing."
            );
          }

          const finalTexts =
            normalizeDocumentTexts({
              ...documentDraftTexts,

              [selectedDocKey]:
                prescriptionContent,
            });

          const medicalActsTotal = medicalActs.reduce(
            (total, act) =>
              total + getActKey(act) * Number(act.cotation || 0),
            0
          );

          const totalAmount =
            Number(consultationPrice || 0) + medicalActsTotal;

          /*
           * FIX: le DTO backend attend `amount` en string
           * ("amount must be a string"). Même format que
           * le reste de l'app (ex. WaitingRoom : "80.000 DT").
           */
          const payload = {
            consultationPrice: Number(consultationPrice || 0),
            amount: `${totalAmount.toFixed(3)} DT`,
            medicalActs: medicalActs.map((act) => ({
              code: act.code,
              designation: act.designation,
              key: getActKey(act),
              cotation: Number(act.cotation || 0),
            })),
          };

          if (medicalActs.length || Number(consultationPrice || 0) > 0) {
            await dispatch(
              updateConsultation({
                consultationId: resolvedConsultationId,
                consultationData: payload,
              })
            ).unwrap();
          }

          const hasDocuments =
            Object.values(
              finalTexts
            ).some(Boolean);

          let result = {
            success:
              true,

            saved:
              {},

            warnings:
              [],
          };

          /*
           * IMPORTANT:
           * API prescription appelée seulement
           * si le médecin a réellement créé
           * un document.
           */
          if (
            hasDocuments
          ) {
            result =
              await handleSavePrescriptionToBackend(
                {
                  documentTexts:
                    finalTexts,
                }
              );

            if (
              result.success ===
              false
            ) {
              throw new Error(
                result.warnings
                  ?.join(
                    "\n"
                  ) ||
                  "Documents could not be saved."
              );
            }
          }

          console.log(
            "✅ CONSULTATION FINALIZED:",
            resolvedConsultationId
          );

          await onSaved?.({
            consultationId:
              resolvedConsultationId,

            patientId:
              resolvedPatientId,

            documents:
              result.saved,
          });

          window.dispatchEvent(
            new CustomEvent("prescriptions-updated", {
              detail: {
                consultationId: resolvedConsultationId,
              },
            })
          );

          onClose?.();
        } catch (
          error
        ) {
          console.error(
            "❌ FINAL SAVE:",
            error
          );

          const backendMessage =
            error?.message ||
            error?.response
              ?.data
              ?.message;

          setSaveConsultationError(
            Array.isArray(backendMessage)
              ? backendMessage.join(", ")
              : backendMessage ||
                  "Unable to save consultation."
          );
        } finally {
          setIsSavingConsultation(
            false
          );
        }
      },
      [
        isSavingConsultation,
        frontOnly,
        resolvedConsultationId,
        resolvedPatientId,
        documentDraftTexts,
        selectedDocKey,
        prescriptionContent,
        medicalActs,
        consultationPrice,
        dispatch,
        handleSavePrescriptionToBackend,
        onSaved,
        onClose,
      ]
    );

  /* =======================================================
     CLOSED
  ======================================================= */

  if (!isOpen) {
    return null;
  }

  /* =======================================================
     MISSING CONSULTATION
  ======================================================= */

  if (
    !resolvedConsultationId &&
    !frontOnly
  ) {
    return (
      <div
        className="consultation-popup-overlay"
        onClick={
          onClose
        }
      >
        <div
          className="consultation-popup-container"
          onClick={(
            event
          ) =>
            event.stopPropagation()
          }
        >
          <div className="consultation-error-overlay">
            <div className="error-icon">
              ❌
            </div>

            <p className="error-title">
              Missing consultation
            </p>

            <p className="error-message">
              Please create a consultation before adding a prescription.
            </p>

            <button
              type="button"
              className="close-btn"
              onClick={
                onClose
              }
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* =======================================================
     UI
  ======================================================= */

  return (
    <div
      className="consultation-popup-overlay"
      onClick={
        isSavingConsultation
          ? undefined
          : onClose
      }
    >
      <div
        className="consultation-popup-container"
        onClick={(
          event
        ) =>
          event.stopPropagation()
        }
      >
        <div className="consultation-content-container step4-inverted">
          <div className="basic-patient-info-section">
            <TreatmentPlan
              onClose={
                onClose
              }

              currentStep={
                4
              }

              onSavePrescription={
                handleSavePrescriptionToBackend
              }

              onPreviewChange={
                handlePreviewChange
              }

              onDocumentTextsChange={
                handleDocumentTextsChange
              }

              onDocKeyChange={
                handleDocKeyChange
              }

              onGenerateDocument={
                handleGenerateDocument
              }

              onClearGeneratedDocument={
                handleClearGeneratedDocument
              }

              onMedicalActsChange={
                handleMedicalActsChange
              }

              onConsultationPriceChange={
                handleConsultationPriceChange
              }

              initialDocumentTexts={
                initialDocumentTexts
              }

              initialDocumentKey={
                initialDocumentKey
              }

              consultationId={
                resolvedConsultationId
              }

              patientId={
                resolvedPatientId
              }

              doctorId={
                doctorId
              }

              patientData={
                patientData
              }

              insuranceData={
                consultationInsuranceData
              }

              currentUser={
                currentUser
              }

              doctorInfo={
                loadedDoctorInfo || doctorInfo
              }

              emergencyData={
                emergencyData
              }

              frontOnly={
                frontOnly
              }
            />
          </div>

          <div className="consultation-details-section">
            <DocumentPreview
              prescriptionContent={
                prescriptionContent
              }

                documentDraftTexts={
                  documentDraftTexts
                }

              docKey={
                selectedDocKey
              }

              generatedDocuments={
                generatedDocuments
              }

              activeGeneratedDocumentId={
                activeGeneratedDocumentId
              }

              onActiveGeneratedDocumentChange={
                setActiveGeneratedDocumentId
              }

                onGenerateDocument={
                  handleGenerateDocument
                }

              onPrintRecord={
                handlePrintRecordClick
              }

              doctorData={
                currentUser
              }

              layout={
                prescriptionLayout
              }

              frontOnly={
                frontOnly
              }
            />
          </div>
        </div>

        {/* ================================================
            FINAL SAVE
        ================================================ */}

        <div className="consultation-final-save">
          {saveConsultationError && (
            <div className="consultation-final-save__error">
              {
                saveConsultationError
              }
            </div>
          )}

          <div className="consultation-final-save__actions">
            <button
              type="button"
              className="consultation-final-save__cancel"
              disabled={
                isSavingConsultation
              }
              onClick={
                onClose
              }
            >
              {t("common.cancel")}
            </button>

            <button
              type="button"
              className="consultation-final-save__button"
              disabled={
                isSavingConsultation
              }
              onClick={
                handleSaveWholeConsultation
              }
            >
              {isSavingConsultation
                ? t("common.saving", { defaultValue: t("common.save") })
                : t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewConsultation;
